// How IDs read in the executive logs (Leon, 2026-10-30).
//
// The rule: a reference names something people already know — a booking, a
// partner, a date. A code that only means something inside the database is
// noise, so it is either rebuilt from what it points at or hidden.
//
//   service reference        the booking + service number   BNB-20261022-7RGJ-S1
//   dispatch reference       its trip reference             BNB-20261022-MZCCGT
//   client                   one short code per client      CLI-CF5094 (same in every tab)
//   partner                  partner code                   MT-ALMA01 / MR-ARIE01
//   rider session            rider + day                    MR-ARIE01 · Oct 22
//   partner movement         what + day                     Cash hand-over · Oct 22
//   client wallet entry      WAL-8AFDE7, hidden on screen: its booking says what it was for
//
// Screen and export show the same. The cell pop-up shows the readable value;
// the raw rows are untouched in the query cache.

const short6 = (text: string) => text.replace(/-/g, "").slice(0, 6).toUpperCase();
const isLongId = (text: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(text);

export type PartnerCodes = ReadonlyMap<string, string>;

const MOVEMENT: Record<string, string> = {
  payroll_earning: "Payroll",
  cash_handoff_out: "Cash hand-over",
  cash_handoff_in: "Cash received",
  cash_collected: "Cash collected",
  cash_retained: "Cash kept",
  payout_settled: "Payout",
};

/** "Oct 22" in Manila time, from a date or a timestamp. */
function day(value: unknown): string {
  if (typeof value !== "string" || !value) return "";
  const date = new Date(value.length === 10 ? `${value}T00:00:00+08:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    timeZone: "Asia/Manila",
  });
}

const text = (value: unknown) => (value == null ? "" : String(value));

function partnerCode(id: unknown, codes: PartnerCodes): string {
  const raw = text(id);
  if (!raw) return "";
  return codes.get(raw) ?? (isLongId(raw) ? "—" : raw);
}

/** A row with every ID in its readable form. Other columns are left as they are. */
export function readableRow(
  row: Record<string, unknown>,
  codes: PartnerCodes,
): Record<string, unknown> {
  const out = { ...row };
  if (isLongId(text(row.client_id))) out.client_id = `CLI-${short6(text(row.client_id))}`;
  if (isLongId(text(row.claimed_by_id)))
    out.claimed_by_id = `CLI-${short6(text(row.claimed_by_id))}`;
  if (isLongId(text(row.entry_id))) out.entry_id = `WAL-${short6(text(row.entry_id))}`;
  if (isLongId(text(row.topup_id))) out.topup_id = `TOP-${short6(text(row.topup_id))}`;
  for (const key of ["staff_id", "rider_id", "therapist_id"] as const) {
    if (key in row) out[key] = partnerCode(row[key], codes);
  }
  if ("rider_session_id" in row) {
    out.rider_session_id = [partnerCode(row.rider_id, codes), day(row.computed_at)]
      .filter(Boolean)
      .join(" · ");
  }
  if ("reference" in row && "entry_type" in row) {
    const what = MOVEMENT[text(row.entry_type)] ?? text(row.entry_type);
    out.reference = [what, day(row.entry_timestamp ?? row.entry_date)].filter(Boolean).join(" · ");
  }
  return out;
}

/**
 * Every row of a log made readable. Services are numbered within their booking
 * (BNB-…-S1, -S2), in the order the log lists them.
 */
export function readableRows<T extends Record<string, unknown>>(
  rows: readonly T[],
  codes: PartnerCodes,
): T[] {
  const perBooking = new Map<string, number>();
  return rows.map((row) => {
    const out: Record<string, unknown> = readableRow(row, codes);
    if ("service_ref" in row && row.booking_ref) {
      const booking = text(row.booking_ref);
      const n = (perBooking.get(booking) ?? 0) + 1;
      perBooking.set(booking, n);
      out.service_ref = `${booking}-S${n}`;
    }
    return out as T;
  });
}
